import type { CallSport } from "@/lib/the-call/types";
import type { CompanionPlanSavedPlaceAction } from "./plan-model";

export type CompanionPlaceOperation = {
  placeId: string;
  placeName: string;
  actionId: string;
  run: () => Promise<{ success: boolean; error?: string }>;
};

export type CompanionPlaceOperationResult = {
  placeId: string;
  placeName: string;
  outcome: "success" | "partial" | "error";
  attempted: number;
  succeeded: number;
  failures: string[];
};

export type CompanionPlaceOperationBatchResult = {
  outcome: "success" | "partial" | "error";
  places: CompanionPlaceOperationResult[];
};

type CompanionEndedTripOperationInput = {
  places: readonly CompanionPlanSavedPlaceAction[];
  keepFollowingBaseId: string | null;
  movePlace: (
    favouriteId: string,
    baseId: string | null,
  ) => Promise<{ success: boolean; error?: string }>;
  mutePlace: (
    slug: string,
    sport: CallSport,
  ) => Promise<{ success: boolean; error?: string }>;
};

export function buildCompanionEndedTripOperations({
  places,
  keepFollowingBaseId,
  movePlace,
  mutePlace,
}: CompanionEndedTripOperationInput): {
  move: CompanionPlaceOperation[];
  mute: CompanionPlaceOperation[];
} {
  return {
    move: places.map((place) => ({
      placeId: place.slug,
      placeName: place.name,
      actionId: "keep-following",
      run: () => movePlace(place.favouriteId, keepFollowingBaseId),
    })),
    mute: places.flatMap((place) =>
      place.followedCallSports.map((sport) => ({
        placeId: place.slug,
        placeName: place.name,
        actionId: `mute:${sport}`,
        run: () => mutePlace(place.slug, sport),
      })),
    ),
  };
}

export async function settleCompanionPlaceOperations(
  operations: readonly CompanionPlaceOperation[],
): Promise<CompanionPlaceOperationBatchResult> {
  const grouped = new Map<string, CompanionPlaceOperationResult>();

  for (const operation of operations) {
    const current = grouped.get(operation.placeId) ?? {
      placeId: operation.placeId,
      placeName: operation.placeName,
      outcome: "success" as const,
      attempted: 0,
      succeeded: 0,
      failures: [],
    };
    current.attempted += 1;

    try {
      const result = await operation.run();
      if (result.success) {
        current.succeeded += 1;
      } else {
        current.failures.push(result.error?.trim() || `${operation.actionId} failed`);
      }
    } catch {
      current.failures.push(`${operation.actionId} failed`);
    }

    current.outcome = current.failures.length === 0
      ? "success"
      : current.succeeded === 0
        ? "error"
        : "partial";
    grouped.set(operation.placeId, current);
  }

  const places = [...grouped.values()];
  const attempted = places.reduce((total, place) => total + place.attempted, 0);
  const succeeded = places.reduce((total, place) => total + place.succeeded, 0);

  return {
    outcome: succeeded === attempted ? "success" : succeeded === 0 ? "error" : "partial",
    places,
  };
}

export function companionPlaceOperationMessage(
  result: CompanionPlaceOperationBatchResult,
  action: string,
): string {
  if (result.places.length === 0) return `No places needed to be ${action}.`;
  const successful = result.places.filter((place) => place.outcome === "success");
  const incomplete = result.places.filter((place) => place.outcome !== "success");

  if (incomplete.length === 0) {
    return `${successful.length} ${successful.length === 1 ? "place" : "places"} ${action}.`;
  }

  const failedNames = incomplete.map((place) => place.placeName).join(", ");
  if (successful.length === 0) {
    return `No places were fully ${action}. Check ${failedNames}.`;
  }
  return `${successful.length} ${successful.length === 1 ? "place was" : "places were"} ${action}. Check ${failedNames}.`;
}
